/**
 * DecisionFlow — PlanVersioningManager
 * ui/src/components/tenant-admin/PlanVersioningManager.jsx
 *
 * Domain: §P4-02 Plan Versioning
 * Nav route: /admin/plan-versioning
 * Session: S541 — Supersede action removed per mastercontext_s541.md Step 4B
 *
 * API:
 *   GET  /api/v1/plan-components  → list all (with version, is_active)
 *
 * S541 change: Supersede action button removed entirely.
 * Version history is display-only — read-only. Supersede is now only reachable
 * via the "Update the Template Design" path in the Create Change dialog on
 * PlanDetailView.jsx (§7 three-path governance dialog).
 *
 * Path: ui/src/components/tenant-admin/PlanVersioningManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import { apiGet } from '../../api/client';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';

// ---------------------------------------------------------------------------
// PlanVersioningManager
// ---------------------------------------------------------------------------

export default function PlanVersioningManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.plan_versioning')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }
  const fpId = period?.fiscal_period_id ?? fiscalPeriodId ?? null;

  const [components, setComponents]   = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError]     = useState('');

  // ---------------------------------------------------------------------------
  // Load plan components
  // ---------------------------------------------------------------------------

  const loadComponents = useCallback(async () => {
    if (!user) return;
    setListLoading(true);
    setListError('');
    try {
      const url = fpId
        ? `/api/v1/plan-components?fiscal_period_id=${fpId}`
        : '/api/v1/plan-components';
      const data = await apiGet(url);
      const all = Array.isArray(data) ? data : data?.data ?? data?.plan_components ?? [];
      // Sort: active first, then by version desc
      all.sort((a, b) => {
        if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
        return (b.version ?? 0) - (a.version ?? 0);
      });
      setComponents(all);
    } catch (error) {
      setListError(getErrorMessage(error, 'Unable to load plan components.'));
    } finally {
      setListLoading(false);
    }
  }, [user, fpId]);

  useEffect(() => { loadComponents(); }, [loadComponents]);

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  if (!user) {
    return (
      <section className="flex flex-col gap-4">
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Grouped display — active vs archived
  // ---------------------------------------------------------------------------

  const activeComponents   = components.filter((c) => c.is_active);
  const historicComponents = components.filter((c) => !c.is_active);

  return (
    <section className="flex flex-col gap-4">

      <div>
        <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">Plan Versioning</h1>
        <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Version history for plan components. To make changes to an approved plan, use the{' '}
          <strong>Create Change</strong> button on the plan detail view.
        </p>
      </div>

      {listLoading && (
        <div className="animate-pulse space-y-2">
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
        </div>
      )}
      {listError && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {listError}
        </div>
      )}

      {!listLoading && !listError && (
        <>
          {/* Active components — read-only display */}
          <div>
            <p className="mb-2 text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
              Active Components
            </p>
            {activeComponents.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-6 text-center">
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No active plan components found for this period.</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Component ID</th>
                      <th className={thClass}>Plan ID</th>
                      <th className={thClass}>Version</th>
                      <th className={thClass}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeComponents.map((c) => (
                      <tr
                        key={c.plan_component_id}
                        className="hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
                      >
                        <td className={tdClass + ' font-mono font-medium'}>{c.plan_component_id}</td>
                        <td className={tdClass}>{c.plan_id ?? '—'}</td>
                        <td className={tdClass}>v{c.version ?? 1}</td>
                        <td className={tdClass}>
                          <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-success))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-success))]">
                            Active
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Version history — read-only */}
          {historicComponents.length > 0 && (
            <div>
              <p className="mb-2 text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
                Version History (Read-Only)
              </p>
              <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Component ID</th>
                      <th className={thClass}>Plan ID</th>
                      <th className={thClass}>Version</th>
                      <th className={thClass}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicComponents.map((c) => (
                      <tr key={c.plan_component_id} className="opacity-70">
                        <td className={tdClass + ' font-mono'}>{c.plan_component_id}</td>
                        <td className={tdClass}>{c.plan_id ?? '—'}</td>
                        <td className={tdClass}>v{c.version ?? 1}</td>
                        <td className={tdClass}>
                          <span className="inline-flex items-center gap-1 rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                            Archived
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

    </section>
  );
}
